import { ClaimsBFFControllerClient } from "./api/protos/Claims-bffServiceClientPb";
import {
	ExportEntityType,
	DynamicExportToCsvRequest,
	DynamicExportToCsvResponse,
	ExportFilterPair
} from "./api/protos/messages/claims.message_pb";
import { getBffRequest } from "./api/base/api";
import { getApiToken } from "./helpers";

/**
 * Options for exporting table data to CSV
 */
export interface ExportOptions {
	entityType: ExportEntityType;
	filters?: Record<string, string | number | boolean>;
	columns?: string[];
	sortField?: string;
	sortDir?: "asc" | "desc";
	limit?: number;
	ids?: number[]; // Specific IDs to export (takes priority over filters)
}

/**
 * Downloads CSV data as a file
 * @param data - The CSV data as Uint8Array
 * @param fileName - The name for the downloaded file
 */
function downloadCsv(data: Uint8Array, fileName: string): void {
	// Convert Uint8Array to Blob
	const blob = new Blob([data], { type: "text/csv;charset=utf-8;" });

	// Create download link
	const url = URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.download = fileName;

	// Trigger download
	document.body.appendChild(link);
	link.click();

	// Cleanup
	document.body.removeChild(link);
	URL.revokeObjectURL(url);
}

/**
 * Universal export function that handles exporting any table type to CSV
 * @param options - Export configuration options
 * @returns Promise<{ totalRecords: number; fileName: string }>
 * @throws Error if the export fails
 */
export async function exportTableToCsv(options: ExportOptions): Promise<{ totalRecords: number; fileName: string }> {
	try {
		const token: string = getApiToken();
		const client: ClaimsBFFControllerClient = getBffRequest(token);

		// Build request
		const request = new DynamicExportToCsvRequest();
		request.setEntityType(options.entityType);

		// Set columns (empty = use defaults)
		if (options.columns && options.columns.length > 0) {
			request.setColumnsList(options.columns);
		}

		// Convert filters to ExportFilterPair[]
		const filterPairs: ExportFilterPair[] = [];

		// If specific IDs are provided, add them as a filter (takes priority)
		if (options.ids && options.ids.length > 0) {
			const idsPair = new ExportFilterPair();
			idsPair.setKey("ids");
			idsPair.setValue(options.ids.join(","));
			filterPairs.push(idsPair);
		} else if (options.filters) {
			// Otherwise use the provided filters
			Object.entries(options.filters)
				.filter(([_, value]) => value !== null && value !== undefined && value !== "")
				.forEach(([key, value]) => {
					const pair = new ExportFilterPair();
					pair.setKey(key);
					pair.setValue(String(value));
					filterPairs.push(pair);
				});
		}

		if (filterPairs.length > 0) {
			request.setFiltersList(filterPairs);
		}

		// Set optional params
		if (options.sortField) {
			request.setSortField(options.sortField);
		}
		if (options.sortDir) {
			request.setSortDir(options.sortDir);
		}
		if (options.limit) {
			request.setLimit(options.limit);
		}

		// Call RPC
		const response: DynamicExportToCsvResponse = await client.dynamicExportToCsv(request, {});

		// Extract CSV data
		const csvData = response.getCsvData_asU8();
		const fileName = response.getFileName();
		const totalRecords = response.getTotalRecords();

		console.log(`Exporting ${totalRecords} records to ${fileName}`);

		// Download CSV
		downloadCsv(csvData, fileName);

		return { totalRecords, fileName };
	} catch (err) {
		const error = err as { code?: number; message?: string; metadata?: unknown };
		console.error("Export failed:", error);
		console.error("Export error details:", {
			code: error.code,
			message: error.message,
			metadata: error.metadata
		});

		// Re-throw with more context but don't trigger auto-logout
		const errorMessage = error.message || "Failed to export data";
		throw new Error(errorMessage);
	}
}
